import React, { useEffect, useState } from "react";
import {
  Bell,
  Briefcase,
  FolderKanban,
  Package,
  Palette,
  Users,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useActionGuard } from "../hooks/useActionGuard.ts";
import type { NotificationHubItem } from "./mapApiNotificationToHubItem.ts";
import type { HubCategory } from "./notificationTypes.ts";
import { formatRelativeHubDate } from "./formatHubDate.ts";
import {
  isPendingPostulationSend,
} from "./notificationPostulationHelpers.ts";
import { resolveNotificationHubNavigateGuardKey } from "./notificationHubGuardKeys.ts";
import styles from "./NotificationHubCard.module.scss";

const categoryIcon: Record<HubCategory, React.ReactNode> = {
  projects: <FolderKanban size={22} strokeWidth={2} />,
  materials: <Package size={22} strokeWidth={2} />,
  account: <Users size={22} strokeWidth={2} />,
  social: <Sparkles size={22} strokeWidth={2} />,
  work: <Briefcase size={22} strokeWidth={2} />,
  portfolio: <Palette size={22} strokeWidth={2} />,
  system: <Bell size={22} strokeWidth={2} />,
};

export type NotificationHubCardProps = {
  item: NotificationHubItem;
  onMarkRead?: (id: string) => void;
  onPostulationAccept?: (item: NotificationHubItem) => void;
  onPostulationReject?: (item: NotificationHubItem) => void;
  onDelete?: (item: NotificationHubItem) => void;
};

const NotificationHubCard: React.FC<NotificationHubCardProps> = ({
  item,
  onMarkRead,
  onPostulationAccept,
  onPostulationReject,
  onDelete,
}) => {
  const navigate = useNavigate();
  const { checkAccess } = useActionGuard();
  const unread = !item.read;
  const [avatarFailed, setAvatarFailed] = useState(false);

  useEffect(() => {
    setAvatarFailed(false);
  }, [item.actorAvatar]);

  const showAvatar =
    Boolean(item.actorAvatar) && !avatarFailed;

  const showPostulationActions =
    isPendingPostulationSend(item) &&
    Boolean(onPostulationAccept) &&
    Boolean(onPostulationReject);

  const canDelete =
    Boolean(onDelete) &&
    item.id !== "" &&
    Number.isFinite(Number(item.id));

  const handleOpen = () => {
    if (!item.deeplink) return;
    if (
      !checkAccess(resolveNotificationHubNavigateGuardKey(item.deeplink))
    ) {
      return;
    }
    if (unread && item.id && onMarkRead) {
      onMarkRead(item.id);
    }
    navigate(item.deeplink);
  };

  const handleDownloadClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!item.downloadUrl) return;
    if (!checkAccess("notification_download")) {
      e.preventDefault();
      return;
    }
    if (unread && item.id && onMarkRead) {
      onMarkRead(item.id);
    }
  };

  const handleMarkReadOnly = () => {
    if (unread && item.id && onMarkRead) {
      onMarkRead(item.id);
    }
  };

  const showFooter =
    Boolean(item.deeplink) ||
    Boolean(item.downloadUrl) ||
    (unread && Boolean(onMarkRead));

  return (
    <article className={`${styles.card} ${unread ? styles.unread : ""}`}>
      <div className={styles.mainRow}>
        <div className={styles.visual}>
          {showAvatar ? (
            <img
              className={styles.avatarImg}
              src={item.actorAvatar as string}
              alt=""
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <div className={styles.iconWrap}>{categoryIcon[item.category]}</div>
          )}
        </div>
        <div className={styles.body}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>{item.title}</h3>
            {canDelete ? (
              <button
                type="button"
                className={styles.deleteBtn}
                aria-label="Eliminar notificación"
                onClick={() => onDelete?.(item)}
              >
                <Trash2 size={18} strokeWidth={2} />
              </button>
            ) : null}
          </div>
          <p className={styles.text}>{item.body}</p>
          {item.details.length > 0 ? (
            <ul className={styles.detailsList}>
              {item.details.map((d, idx) => (
                <li
                  key={`${idx}-${d.text}`}
                  className={
                    d.variant === "chip" ? styles.detailChip : styles.detailText
                  }
                >
                  {d.text}
                </li>
              ))}
            </ul>
          ) : null}
          <div className={styles.meta}>
            <span className={styles.date}>
              {formatRelativeHubDate(item.createdAt)}
            </span>
          </div>
        </div>
      </div>
      {showPostulationActions ? (
        <div className={styles.postulationBar}>
          <button
            type="button"
            className={styles.rejectPostBtn}
            onClick={() => onPostulationReject?.(item)}
          >
            Rechazar solicitud
          </button>
          <button
            type="button"
            className={styles.acceptPostBtn}
            onClick={() => onPostulationAccept?.(item)}
          >
            Aceptar solicitud
          </button>
        </div>
      ) : null}
      {showFooter ? (
        <div className={styles.footerBar}>
          {item.deeplink ? (
            <button type="button" className={styles.linkBtn} onClick={handleOpen}>
              {item.actionLabel}
            </button>
          ) : null}
          {item.downloadUrl ? (
            <a
              href={item.downloadUrl}
              className={styles.downloadLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleDownloadClick}
            >
              {item.downloadLabel ?? "Descargar archivo"}
            </a>
          ) : null}
          {unread ? (
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={handleMarkReadOnly}
            >
              Marcar leída
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
};

export default NotificationHubCard;
